/**
 * What time it is in the world.
 *
 * A day is a fraction rather than a clock face: nothing in the game wants to
 * know it is twenty past four, and everything that wants to know anything wants
 * to know whether it is light. So the day runs from nought to one and is cut
 * into four named stretches.
 *
 * It is read from the wall clock rather than counted in ticks, so that a world
 * coming back up after a restart comes back at the same time of day it went
 * down at, and two shards agree with each other without saying a word.
 */

/** How long a whole day takes, in real milliseconds. */
export const DAY_MS = 60 * 60 * 1000;

export type DayPhase = 'dawn' | 'day' | 'dusk' | 'night';

export const DAY_PHASES: readonly DayPhase[] = ['dawn', 'day', 'dusk', 'night'];

/** What each stretch is called where somebody reads it. */
export const PHASE_NAMES: Readonly<Record<DayPhase, string>> = {
  dawn: 'Dawn',
  day: 'Daytime',
  dusk: 'Dusk',
  night: 'Night',
};

/**
 * Where each stretch begins, as a fraction of the day.
 *
 * Daylight is most of it and the two turns are short, which is what a day feels
 * like and also what makes dawn and dusk worth writing anything for: something
 * that only happens at dusk should be something somebody has to be there for.
 */
const STARTS: readonly { at: number; phase: DayPhase }[] = [
  { at: 0, phase: 'dawn' },
  { at: 0.1, phase: 'day' },
  { at: 0.6, phase: 'dusk' },
  { at: 0.7, phase: 'night' },
];

/** How far through the day a moment is, from nought at first light. */
export function dayFraction(now: number, dayMs = DAY_MS): number {
  const held = Math.max(1, dayMs);
  return (((now % held) + held) % held) / held;
}

/** Which stretch of the day a fraction falls in. */
export function phaseAt(fraction: number): DayPhase {
  const held = ((fraction % 1) + 1) % 1;

  let found: DayPhase = 'night';
  for (const start of STARTS) {
    if (held >= start.at) found = start.phase;
  }

  return found;
}

/** The stretch a moment falls in, which is what everything actually asks. */
export function phaseNow(now: number, dayMs = DAY_MS): DayPhase {
  return phaseAt(dayFraction(now, dayMs));
}

/**
 * How light it is, from nought in the dead of night to one at noon.
 *
 * Eased across the two turns rather than stepped, because a sky that changes
 * between one frame and the next reads as a fault rather than as a sunset.
 */
export function daylight(fraction: number): number {
  const held = ((fraction % 1) + 1) % 1;

  if (held < 0.1) return ease(held / 0.1);
  if (held < 0.6) return 1;
  if (held < 0.7) return ease(1 - (held - 0.6) / 0.1);
  return 0;
}

function ease(held: number): number {
  const clamped = Math.min(1, Math.max(0, held));
  return clamped * clamped * (3 - 2 * clamped);
}
